import toast from "react-hot-toast";

import { HTTP_STATUS } from "@/config/http";
import type { UpgradeReason } from "@/config/plans";
import { useAuthStore } from "@/stores/AuthStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";
import { useUpgradeStore } from "@/stores/UpgradeStore";
import { refreshSession } from "@/Utils/Auth/authSession";
import { createTokenRefresher } from "@/Utils/Auth/createTokenRefresher";
import { loggerService, LogTag } from "@/Utils/LoggerService";

import { namedBlobFromResponse } from "./contentDisposition";
import { translateErrorCode } from "./errorCodes";

// Singleton refresh guard: concurrent 401s trigger a single cookie refresh.
// The browser carries the auth tokens in httpOnly cookies, so the refresh
// endpoint rotates and re-sets them server-side — nothing is read in JS.
const tryRefreshTokens = createTokenRefresher(() => refreshSession());

// Only the organisation context header is attached client-side; the auth tokens
// ride in the httpOnly cookies sent via credentials: "include".
function buildHeaders(orgId: number | undefined, base?: HeadersInit): Headers {
  const headers = new Headers(base ?? undefined);

  if (orgId && !headers.has("x-organisation-id")) {
    headers.set("x-organisation-id", String(orgId));
  }

  return headers;
}

/**
 * What `appFetch` returns instead of a payload when the call did not succeed.
 * These are plain objects, so a bare `if (result)` reads them as success —
 * check with {@link isApiFailure} before trusting a response.
 */
export interface ApiFailure {
  unauthorized?: boolean;
  forbidden?: boolean;
  quotaExceeded?: boolean;
}

const FAILURE_MARKERS = [
  "unauthorized",
  "forbidden",
  "quotaExceeded",
] as const satisfies readonly (keyof ApiFailure)[];

/**
 * True when `appFetch` returned nothing usable: no body, or one of the failure
 * markers above. Callers that show a success message must gate on this —
 * otherwise a refused call (403, 402…) still reads as a win and reports
 * "undefined items sent".
 */
export function isApiFailure(result: unknown): result is ApiFailure | null {
  if (result === null || result === undefined) {
    return true;
  }

  if (typeof result !== "object") {
    return false;
  }

  return FAILURE_MARKERS.some((marker) => marker in result);
}

async function appFetch(route: string, options?: RequestInit) {
  try {
    const { clearUser } = useAuthStore.getState();
    const { clearOrganisation } = useOrganisationStore.getState();

    const handleSessionExpired = () => {
      clearUser();
      clearOrganisation();
      return { unauthorized: true };
    };

    const { organisation } = useOrganisationStore.getState();
    const response = await fetch(route, {
      ...options,
      headers: buildHeaders(organisation?.org_id, options?.headers),
      credentials: "include",
    });

    if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
      toast.error("Trop de requêtes, veuillez réessayer plus tard");
      return null;
    }

    // On 401, attempt one refresh (via the refresh cookie) + retry.
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      const refreshed = await tryRefreshTokens();
      if (!refreshed) {
        return handleSessionExpired();
      }

      const { organisation: newOrg } = useOrganisationStore.getState();
      const retryResponse = await fetch(route, {
        ...options,
        headers: buildHeaders(newOrg?.org_id, options?.headers),
        credentials: "include",
      });

      if (retryResponse.status === HTTP_STATUS.UNAUTHORIZED) {
        return handleSessionExpired();
      }

      if (retryResponse.ok) {
        const retryContentType = retryResponse.headers.get("content-type");
        if (
          retryContentType?.includes("application/pdf") ||
          retryContentType?.includes("text/csv")
        ) {
          return await namedBlobFromResponse(retryResponse);
        }
        return await retryResponse.json();
      }

      return null;
    }

    const contentType = response.headers.get("content-type");

    if (response.ok) {
      if (
        contentType?.includes("application/pdf") ||
        contentType?.includes("text/csv")
      ) {
        return await namedBlobFromResponse(response);
      }
      return await response.json();
    }

    const parseResponseData = async () => {
      if (!contentType?.includes("application/json")) return null;
      return await response.json();
    };

    const responseData = await parseResponseData();
    loggerService.error(LogTag.API, "API request failed", {
      route,
      responseData,
      status: response.status,
    });

    const firstDetail =
      Array.isArray(responseData?.details) && responseData.details.length > 0
        ? (responseData.details[0] as { message?: string }).message
        : null;

    // A refusal the customer is meant to read, phrased in their language.
    // The backend writes `message` in English — right for logs and for API
    // consumers, wrong for a red toast in a French interface — so business
    // refusals travel with a stable `code` the client can translate. Anything
    // without one still falls back to the message.
    const translatedDetail = translateErrorCode(responseData);

    // A quota breach is not a failure to shout about: the upgrade prompt takes
    // over and explains what to do next, so no toast is raised here.
    if (response.status === HTTP_STATUS.PAYMENT_REQUIRED) {
      const quota = responseData?.details as UpgradeReason | undefined;

      if (quota) {
        useUpgradeStore.getState().openUpgrade(quota);
      } else {
        toast.error(responseData?.message ?? "Une erreur est survenue");
      }

      return { quotaExceeded: true };
    }

    if (response.status === HTTP_STATUS.FORBIDDEN) {
      toast.error(
        translatedDetail ??
          firstDetail ??
          responseData?.message ??
          "Accès refusé",
      );
      return { forbidden: true };
    }

    toast.error(
      translatedDetail ??
        firstDetail ??
        responseData?.message ??
        "Une erreur est survenue",
    );
    return null;
  } catch (error) {
    loggerService.error(LogTag.API, "Unexpected fetch error", error);
    toast.error("Une erreur est survenue");
    return null;
  }
}

export { appFetch };
export default appFetch;
